import { reportingPeriod } from '../utils/calendar.mjs';
import { profitabilityReport, scopedPilotageState } from './profitability.mjs';
import { invoiceReport } from './invoice-report.mjs';
import { prospectingReport } from "./prospecting.mjs";
import { activeContracts } from "./contracts.mjs";
import { agencyMetrics } from "./agency.mjs";
import {
  managementScope,
  managementUsers,
  managementAlerts,
  weeklyGoalReport,
  weeklyLabels,
} from "./management.mjs";
import { managerMetrics } from "./analytics.mjs";
import { getDailyGoals } from "./daily.mjs";
import {
  getWorkHours,
  dailyWorkRows,
  workHoursReport,
  formatWorkDuration,
} from "./work-hours.mjs";
import { formatTimestamp } from "../utils/timestamps.mjs";
export function buildPilotageReport(
  state,
  filters,
  anchor,
  hoursFilters,
  now = new Date(),
) {
  const financialState = state;
  if(filters.level) state = scopedPilotageState(state,filters);
  const m = managerMetrics(state, filters);
  const owners = [
    ...new Map(state.clients.map((c) => [c.ownerId, c.owner])).entries(),
  ].filter(([id]) => !filters.owner || filters.owner === id);
  const name = (id) => state.clients.find((c) => c.id === id)?.name || "";
  const sections = [];
  const add = (title, headers, rows) => sections.push({ title, headers, rows });
  add(
    "Périmètre",
    ["Paramètre", "Valeur"],
    [
      ["Exporté le", formatTimestamp(now.toISOString())],
      ["Dimension", filters.dimension === "individual" ? "Suivi individuel" : filters.dimension === "operations" ? "Suivi opérationnel" : "Pilotage de l’agence"],
      ["Du", filters.from],
      ["Au", filters.to],
      ["Utilisateur", filters.owner || "Tous"],
      ["Agence", filters.agency || "Toutes"],
      ["Source", filters.source || "Toutes"],
      ["Secteur", filters.sector || "Tous"],
      ["Heures du", hoursFilters.from],
      ["Heures au", hoursFilters.to],
      ["Utilisateur heures", hoursFilters.owner || "Tous"],
      [
        "Méthode heures supplémentaires",
        "Dépassements quotidiens des horaires habituels, hors pauses, à vérifier ; journées non confirmées exclues.",
      ],
    ],
  );
  add(
    "Objectifs fixes",
    [
      "Utilisateur",
      "Appels par jour",
      "Décideurs par jour",
      "Besoins par jour",
      "Devis par jour",
    ],
    owners.map(([id, label]) => {
      const g = getDailyGoals(state, id);
      return [
        label,
        g.callResults,
        g.decisionMakersReached,
        g.needsDetected,
        g.quotesSent,
      ];
    }),
  );
  add(
    "Performances utilisateurs",
    ["Utilisateur", "Indicateur", "Valeur"],
    owners.flatMap(([id, label]) => {
      const x = managerMetrics(state, { ...filters, owner: id });
      return [...x.activity, ...x.outcomes].map(([key, value]) => [
        label,
        key,
        value,
      ]);
    }),
  );
  add("Activité", ["Indicateur", "Valeur"], m.activity);
  add("Résultats", ["Indicateur", "Valeur"], m.outcomes);
  const hoursOwners = [
    ...new Map(state.clients.map((c) => [c.ownerId, c.owner])).entries(),
  ].filter(([id]) => !hoursFilters.owner || hoursFilters.owner === id);
  add(
    "Calcul heures",
    [
      "Utilisateur",
      "Jours déclarés",
      "Heures déclarées",
      "Heures habituelles comparables",
      "Dépassement à vérifier",
      "Jours sans référence",
    ],
    hoursOwners.map(([id, label]) => {
      const r = workHoursReport(state, id, hoursFilters.from, hoursFilters.to);
      return [
        label,
        r.days.length,
        formatWorkDuration(r.actual),
        r.days.length > r.unclassified
          ? formatWorkDuration(r.expected)
          : "Non calculable",
        r.days.length > r.unclassified
          ? formatWorkDuration(r.extra)
          : "Non calculable",
        r.unclassified,
      ];
    }),
  );
  add(
    "Heures quotidiennes",
    [
      "Utilisateur",
      "Date",
      "Heures déclarées",
      "Heures habituelles",
      "Dépassement à vérifier",
    ],
    hoursOwners.flatMap(([id, label]) =>
      workHoursReport(state, id, hoursFilters.from, hoursFilters.to).days.map(
        (d) => [
          label,
          d.date,
          formatWorkDuration(d.actual),
          d.expected === null
            ? "Non renseignées"
            : formatWorkDuration(d.expected),
          d.extra === null ? "Non calculable" : formatWorkDuration(d.extra),
        ],
      ),
    ),
  );
  add(
    "Horaires utilisateurs",
    [
      "Utilisateur",
      "Date",
      "Début",
      "Fin",
      "Pause début",
      "Pause fin",
      "Statut",
    ],
    dailyWorkRows(state, hoursOwners, hoursFilters.from, hoursFilters.to).map(
      ({ name, date, hours: h }) => [
        name,
        date,
        h.off ? "Non travaillé" : h.start,
        h.off ? "" : h.end,
        h.off ? "" : h.lunchStart,
        h.off ? "" : h.lunchEnd,
        h.source === "confirmed"
          ? "Confirmés"
          : h.source === "usual"
            ? "Habituels à confirmer"
            : "Non renseignés",
      ],
    ),
  );
  add(
    "Horaires habituels",
    [
      "Utilisateur",
      "Applicable depuis",
      "Jour",
      "Début",
      "Fin",
      "Pause début",
      "Pause fin",
    ],
    (state.workSchedules || [])
      .filter((s) => owners.some(([id]) => id === s.userId))
      .flatMap((s) =>
        [0, 1, 2, 3, 4, 5, 6].map((day) => {
          const h = s.weekdays?.[day] || {
            ...s.hours,
            off: !s.days.includes(day),
          };
          return [
            owners.find(([id]) => id === s.userId)?.[1],
            s.from,
            ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"][day],
            h.off ? "Non travaillé" : h.start,
            h.off ? "" : h.end,
            h.off ? "" : h.lunchStart,
            h.off ? "" : h.lunchEnd,
          ];
        }),
      ),
  );
  add(
    "Engagements non tenus",
    ["Client", "Engagement", "Échéance", "Créé le"],
    m.engagements.map((e) => [
      name(e.clientId),
      e.title,
      e.due,
      formatTimestamp(e.createdAt),
    ]),
  );
  add(
    "Actions en retard",
    [
      "Client",
      "Action",
      "Catégorie",
      "Échéance initiale",
      "Date reportée",
      "Créé le",
    ],
    m.pending.map((t) => [
      name(t.clientId),
      t.title,
      t.category,
      t.due_date_original,
      t.scheduled_date,
      formatTimestamp(t.createdAt),
    ]),
  );
  add(
    "Clients à risque",
    [
      "Client",
      "Commercial",
      "Statut",
      "Motif",
      "Score",
      "Motif perte",
      "Perte enregistrée le",
    ],
    m.risk.map((c) => [
      c.name,
      c.owner,
      c.status,
      c.riskReason || c.riskReasons?.join(" · "),
      c.score,
      c.lossReason,
      formatTimestamp(c.lossReasonAt),
    ]),
  );
  add(
    "Appels détaillés",
    ["Client", "Résultat", "Horodatage", "Décideur joint"],
    m.results.map((r) => [
      name(r.clientId),
      r.label,
      formatTimestamp(r.createdAt),
      r.deciderJoined ? "Oui" : "Non",
    ]),
  );
  add(
    "Événements détaillés",
    ["Client", "Événement", "Horodatage"],
    m.events.map((e) => [
      name(e.clientId),
      e.label,
      formatTimestamp(e.createdAt),
    ]),
  );
  const team = managementUsers(state).filter(
    ([id]) =>
      (!filters.owner || id === filters.owner) &&
      (!filters.agency ||
        state.clients.some(
          (c) => c.ownerId === id && c.agency === filters.agency,
        )),
  );
  add(
    "Objectifs hebdomadaires",
    [
      "Utilisateur",
      "Semaine",
      "Objectif",
      "Base",
      "Report entrant",
      "Total attendu",
      "Réalisé",
      "Reste",
      "Report activé",
    ],
    team.flatMap(([id, label]) => {
      const report = weeklyGoalReport(state, id, anchor);
      return report
        ? report.factors.map((f) => [
            label,
            report.from,
            weeklyLabels[f.key],
            f.base,
            f.carry,
            f.target,
            f.actual,
            f.remaining,
            report.plan.carry ? "Oui" : "Non",
          ])
        : [[label, "", "Non fixés"]];
    }),
  );
  add(
    "Alertes équipe",
    ["Utilisateur", "Urgence", "Alerte", "Nombre"],
    managementAlerts(managementScope(state, filters.agency), team, now).map(
      (a) => [
        a.name,
        a.severity === "high" ? "Prioritaire" : "À suivre",
        a.title,
        a.items.length,
      ],
    ),
  );
  const agency = agencyMetrics(state, filters, now);
  add(
    "Vue agence",
    ["Indicateur", "Valeur"],
    [
      ["Besoins ouverts", agency.open.length],
      ["Démarrages à vérifier", agency.starts.length],
      ["Signés à transmettre", agency.signedPending.length],
      ["Dossiers incomplets", agency.missing.length],
      ["CA connu", agency.caCoverage ? agency.ca : "Non renseigné"],
      [
        "Marge connue",
        agency.marginCoverage ? agency.margin : "Non renseignée",
      ],
      ["Impayés à échéance", agency.unpaid],
      ...agency.types.map((t) => [
        t.type,
        `${t.needs} besoins · ${t.positions} postes`,
      ]),
    ],
  );
  add(
    "Besoins et recrutement",
    [
      "Client",
      "Métier",
      "Type",
      "Postes",
      "Statut",
      "Démarrage",
      "Transmission",
      "Informations manquantes",
    ],
    agency.active.map((n) => [
      agency.clients.find((c) => c.id === n.clientId)?.name,
      n.job,
      n.type,
      n.qty,
      n.status,
      n.start,
      formatTimestamp(n.transmittedAt),
      agency.handoffs
        .find((h) => h.needId === n.id)
        ?.missingFields?.join(", ") || "",
    ]),
  );
  const contracts = activeContracts(state, filters);
  add("Contrats en cours", ["Type", "Nombre sur la période"], contracts.types.map(c=>[c.type,c.count]));
  add("Détail contrats", ["Client", "Commercial", "Référence", "Type", "Début", "Fin", "Statut"], contracts.records.map(c=>[c.client,c.owner,c.label||c.id,c.type,c.start,c.end||"Sans date de fin",c.status]));
  const prospecting = prospectingReport(state, filters, now);
  add("Prospection par commercial", ["Commercial", "Appels", "Établissements", "Décideurs", "Sans réponse", "Besoins", "Devis", "Relances"], prospecting.team.map(t=>[t.name,t.actual.callResults,t.companies,t.actual.decisionMakersReached,t.noResponse,t.actual.needsDetected,t.actual.quotesSent,t.callbacks.length]));
  add("Prospection quotidienne", ["Date", "Appels", "Décideurs joints"], prospecting.days.map(d=>[d.date,d.calls,d.deciders]));
  add("Établissements appelés", ["Client", "Commercial", "Appels", "Dernier résultat", "Horodatage", "Prochaine action", "Échéance"], prospecting.companies.map(c=>[c.client?.name,c.client?.owner,c.calls,c.last.label,formatTimestamp(c.last.createdAt),c.next?.title,c.next?.due_date_original]));
  add("Heures supp déclarées", ["Utilisateur", "Jour", "Début", "Fin", "Pause début", "Pause fin", "Durée habituelle", "Durée déclarée", "Supplément", "Déclaré le"], hoursOwners.flatMap(([id,name])=>workHoursReport(state,id,hoursFilters.from,hoursFilters.to,now).days.filter(d=>d.source==="confirmed" && d.extra>0).map(d=>[name,d.date,d.start,d.end,d.lunchStart,d.lunchEnd,formatWorkDuration(d.expected),formatWorkDuration(d.actual),formatWorkDuration(d.extra),formatTimestamp(d.declaredAt)])));
  const invoices = invoiceReport(state, filters, now);
  for (const [title, rows] of [['Factures HT agences',invoices.agencies],['Factures HT commerciaux',invoices.users]]) add(title,['Nom','Factures période','Total HT connu','HT manquants','Impayées toutes dates','Solde HT connu','Soldes HT manquants'],rows.map(r=>[r.label,r.count,r.ht,r.unknown,r.unpaid,r.balance,r.unknownBalance]));
  add('Impayés détail',['Facture','Client','Agence','Commercial','Émission','Échéance','Solde HT','Situation'],invoices.unpaid.map(r=>[r.reference,r.client,r.agency,r.owner,r.date,r.due,r.remainingHT ?? 'Non renseigné',r.overdue ? 'En retard':r.status]));
  if (filters.level) {
    const p = profitabilityReport(financialState, filters, now);
    add('Synthèse rentabilité',['Information'],p.summary.map(s=>[s]));
    add('KPI décision',['Indicateur','Réalisé','Objectif','Attendu à date','Écart','Projection','Écart projeté'],p.kpis.map(k=>[k.key,k.actual ?? 'Non disponible',k.target ?? 'Non disponible',k.expected ?? 'Non disponible',k.gap ?? 'Non disponible',k.projection ?? 'Non disponible',k.projectedGap ?? 'Non disponible']));
    const businessFilters={...filters,period:filters.businessPeriod || filters.period,...reportingPeriod(filters.businessPeriod || filters.period,anchor)};
    const business=profitabilityReport(financialState,businessFilters,now);
    add('Activité par contrat',['Contrat','Objectif HT','Réalisé HT','Rythme %','Projection HT','Marge'],business.types.map(t=>[t.type,t.target ?? 'Non disponible',t.actual ?? 'Non disponible',t.rhythm ?? 'Non disponible',t.projection ?? 'Non disponible',t.margin ?? 'Non disponible']));
    add('Rentabilité clients',['Client','Commercial','Contrat','CA HT','Marge','Taux %','Heures','Évolution CA %','Diagnostic'],p.clientRows.map(r=>[r.client.name,r.client.owner,r.type,r.ca ?? 'Non disponible',r.margin ?? 'Non disponible',r.rate ?? 'Non disponible',r.hours ?? 'Non disponible',r.evolution ?? 'Non disponible',r.diagnostic]));
    add('Prévisions',['Catégorie','Valeur'],['realized','secured','probable','total','target','gap'].map(key=>[key,p.forecast[key] ?? 'Non disponible']));
    add('Dossiers prévisionnels',['Catégorie','Client','Référence','Montant HT','Probabilité','Date'],['realized','secured','probable'].flatMap(key=>p.forecast.calculationDetails[key].map(r=>[key,name(r.clientId),r.id,r.amountHT ?? r.ca,r.probability,r.date])));
    for(const [title,rows] of [['Secteurs financiers',p.sectors],['Métiers financiers',p.jobs]]) add(title,['Nom','CA HT','Marge','Taux %'],rows.map(r=>[r.label,r.ca ?? 'Non disponible',r.margin ?? 'Non disponible',r.rate ?? 'Non disponible']));
    add('Activité perdue',['Client','Cause','Type','CA potentiel','Marge potentielle'],p.losses.map(r=>[name(r.clientId),r.reason,r.kind,r.amountHT ?? 'Non disponible',r.marginHT ?? 'Non disponible']));
    add('Sources financières',['Client','Source','Date','Contrat','CA HT','Marge','Heures réalisées','Heures facturées','Coefficient'],p.records.map(r=>[r.client,r.source,r.date,r.type,r.ca ?? 'Non disponible',r.margin ?? 'Non disponible',r.hours ?? 'Non disponible',r.hoursBilled ?? 'Non disponible',r.coefficient ?? 'Non disponible']));
    add('Activation clients',['Client','Situation'],[...p.activated.map(id=>[name(id),'A commandé après signature']),...p.inactive.map(id=>[name(id),'Signé sans commande, toutes dates'])]);
    add('Actions rentabilité',['Sujet','Client','Dossier'],p.actions.flatMap(a=>a.items.length?a.items.map(i=>[a.label,name(i.clientId),i.id || '']):[[a.label,'','']]));
    add('Dépendance clients',['Client','CA HT connu','Part %'],p.concentration.map(r=>[r.client.name,r.ca,p.ca>0?r.ca/p.ca*100:'Non disponible']));
    add('Évolution rentabilité',['Client','Contrat','CA précédent','Marge précédente','Heures précédentes','CA N-1','Dernière commande'],p.clientRows.map(r=>[r.client.name,r.type,r.oldCA ?? 'Non disponible',r.oldMargin ?? 'Non disponible',r.oldHours ?? 'Non disponible',r.yearAgoCA ?? 'Non disponible',r.lastOrder || 'Non disponible']));
    add('Objectifs financiers',['Niveau','Entité','Contrat','Période','Début','CA','Marge','Taux %','Modifié le'],(state.financialGoals || []).filter(g=>g.level===filters.level && g.entity===(filters.owner || filters.agency || '') && g.period===filters.period && g.from===filters.from).map(g=>[g.level,g.entity,g.contract || 'Tous',g.period,g.from,g.ca,g.margin,g.marginRate,formatTimestamp(g.updatedAt)]));
  }
  return sections;
}
export async function exportPilotage(format, sections, filename) {
  if (format === "excel") {
    const { default: ExcelJS } = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    for (const section of sections) {
      const sheet = workbook.addWorksheet(section.title.slice(0, 31));
      sheet.addRow(section.headers);
      sheet.addRows(section.rows.map((row) => row.map((v) => v ?? "")));
      sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      sheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF174BB0" },
      };
      sheet.views = [{ state: "frozen", ySplit: 1 }];
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: {
          row: Math.max(1, sheet.rowCount),
          column: section.headers.length,
        },
      };
      sheet.columns.forEach((column) => {
        column.width = 28;
        column.alignment = { vertical: "top", wrapText: true };
      });
    }
    const blob = new Blob([await workbook.xlsx.writeBuffer()], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename + ".xlsx";
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } else {
    const [{ jsPDF }, { autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);
    const doc = new jsPDF({ orientation: "landscape" });
    sections.forEach((section, index) => {
      if (index) doc.addPage();
      doc.setFontSize(15);
      doc.text("JM INTERIM · " + section.title, 14, 16);
      autoTable(doc, {
        startY: 24,
        head: [section.headers],
        body: section.rows.length
          ? section.rows.map((row) =>
              row.map((v) => String(v ?? "").replace(/\u202f|\u00a0/g, " ")),
            )
          : [section.headers.map((_, i) => (i === 0 ? "Aucune donnée" : ""))],
        styles: { fontSize: 9, overflow: "linebreak" },
        headStyles: { fillColor: [23, 75, 176] },
        margin: { bottom: 18 },
      });
    });
    for (let i = 1; i <= doc.getNumberOfPages(); i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.text(`${i} / ${doc.getNumberOfPages()}`, 275, 200);
    }
    doc.save(filename + ".pdf");
  }
}
